import { Controller } from "@hotwired/stimulus"

// Sidebar Navigation Controller
// Handles sidebar collapse/expand, search, and mobile menu
export default class extends Controller {
  static targets = ["sidebar", "overlay", "nav", "searchInput", "navItem", "searchClear"]
  static values = {
    collapsed: { type: Boolean, default: false }
  }

  connect() {
    this.navScrollRestored = false

    // Load saved sidebar state from localStorage (only on desktop)
    if (window.innerWidth >= 1024) {
      const savedState = localStorage.getItem('sidebarCollapsed')
      if (savedState !== null) {
        this.collapsedValue = savedState === 'true'
        this.updateSidebarState()
      }
    } else {
      // On mobile, always start expanded (so it has full width when opened)
      this.collapsedValue = false
      document.documentElement.classList.remove('sidebar-state-collapsed')
      this.sidebarTarget.classList.remove('sidebar-collapsed')
      this.sidebarTarget.classList.add('sidebar-expanded')
    }

    this.applyGroupStates()
    this.restoreNavScroll()
    this.bindNavScroll()

    // Close sidebar on mobile when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.handleClickOutside)

    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this)
    document.addEventListener('turbo:before-cache', this.handleBeforeCache)
    window.addEventListener('beforeunload', this.handleBeforeUnload)

    requestAnimationFrame(() => {
      document.documentElement.classList.remove('sidebar-state-initializing')
    })
  }

  disconnect() {
    this.persistNavScroll()
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)

    if (this.hasNavTarget && this.handleNavScroll) {
      this.navTarget.removeEventListener('scroll', this.handleNavScroll)
    }

    if (this.hasNavTarget && this.handleNavClick) {
      this.navTarget.removeEventListener('click', this.handleNavClick, { capture: true })
    }
  }

  // Toggle sidebar collapse/expand
  toggleCollapse() {
    this.collapsedValue = !this.collapsedValue
    this.updateSidebarState()
    localStorage.setItem('sidebarCollapsed', this.collapsedValue.toString())
    this.persistCollapsedCookie()
  }

  // Expand sidebar if collapsed (for search icon click)
  expandIfCollapsed(event) {
    if (this.collapsedValue) {
      this.collapsedValue = false
      this.updateSidebarState()
      localStorage.setItem('sidebarCollapsed', 'false')
      this.persistCollapsedCookie()
      // Focus search input after expanding
      if (this.hasSearchInputTarget) {
        setTimeout(() => this.searchInputTarget.focus(), 100)
      }
    }
  }

  // Open sidebar (mobile)
  open(event) {
    if (event) event.stopPropagation()

    this.sidebarTarget.classList.add('sidebar-mobile-open', 'sidebar-expanded')
    this.sidebarTarget.classList.remove('sidebar-collapsed')
    this.sidebarTarget.style.transform = ''

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove('hidden')
    }
    document.body.style.overflow = 'hidden'
  }

  // Close sidebar (mobile)
  close(event) {
    if (event) event.stopPropagation()

    this.sidebarTarget.classList.remove('sidebar-mobile-open')

    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add('hidden')
    }
    document.body.style.overflow = ''
  }

  // Handle clicks outside sidebar on mobile
  handleClickOutside(event) {
    if (window.innerWidth >= 1024) return // Only on mobile

    const sidebar = this.sidebarTarget
    if (sidebar.classList.contains('sidebar-mobile-open') && !sidebar.contains(event.target)) {
      this.close()
    }
  }

  // ============================================
  // Collapsible navigation groups (compact sidebar)
  // ============================================

  // Default: only "today" (daily work) starts open. The group holding the active
  // page is always opened so users can see where they are. Explicit user
  // choices are remembered per group.
  applyGroupStates() {
    const saved = this.savedGroupStates()
    this.element.querySelectorAll('[data-sidebar-group]').forEach(group => {
      const name = group.dataset.sidebarGroup
      const toggle = group.querySelector('.sidebar-group-toggle')
      if (!toggle) return // non-collapsible group (e.g. settings)

      let collapsed = name in saved ? saved[name] : name !== 'today'
      if (group.querySelector('.sidebar-nav-item.active')) collapsed = false

      group.classList.toggle('sidebar-group--collapsed', collapsed)
      toggle.setAttribute('aria-expanded', String(!collapsed))
    })
  }

  toggleGroup(event) {
    const group = event.currentTarget.closest('[data-sidebar-group]')
    if (!group) return

    const collapsed = group.classList.toggle('sidebar-group--collapsed')
    event.currentTarget.setAttribute('aria-expanded', String(!collapsed))

    const saved = this.savedGroupStates()
    saved[group.dataset.sidebarGroup] = collapsed
    try {
      localStorage.setItem('sidebarGroupsCollapsed', JSON.stringify(saved))
    } catch (_) { /* storage unavailable */ }
  }

  savedGroupStates() {
    try {
      return JSON.parse(localStorage.getItem('sidebarGroupsCollapsed')) || {}
    } catch (_) {
      return {}
    }
  }

  // Search navigation items
  search(event) {
    const query = event.target.value.toLowerCase().trim()

    // While searching, matches must surface even from collapsed groups
    this.element.classList.toggle('sidebar-searching', query !== '')

    if (query === '') {
      // Show all items
      this.navItemTargets.forEach(item => {
        item.classList.remove('hidden')
        // Show parent groups
        const parentGroup = item.closest('[data-sidebar-group]')
        if (parentGroup) {
          parentGroup.classList.remove('hidden')
        }
      })

      // Hide clear button
      if (this.hasSearchClearTarget) {
        this.searchClearTarget.classList.add('hidden')
      }
      return
    }

    // Show clear button
    if (this.hasSearchClearTarget) {
      this.searchClearTarget.classList.remove('hidden')
    }

    // Filter items
    let hasVisibleItems = false
    this.navItemTargets.forEach(item => {
      const text = item.textContent.toLowerCase()
      const keywords = item.dataset.searchKeywords?.toLowerCase() || ''

      if (text.includes(query) || keywords.includes(query)) {
        item.classList.remove('hidden')
        hasVisibleItems = true

        // Show parent group
        const parentGroup = item.closest('[data-sidebar-group]')
        if (parentGroup) {
          parentGroup.classList.remove('hidden')
        }
      } else {
        item.classList.add('hidden')
      }
    })

    // Hide groups with no visible items
    document.querySelectorAll('[data-sidebar-group]').forEach(group => {
      const visibleItems = group.querySelectorAll('[data-sidebar-target="navItem"]:not(.hidden)')
      if (visibleItems.length === 0) {
        group.classList.add('hidden')
      }
    })
  }

  // Clear search
  clearSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ''
      this.search({ target: this.searchInputTarget })
      this.searchInputTarget.focus()
    }
  }

  // Update sidebar UI based on collapsed state
  updateSidebarState() {
    const sidebar = this.sidebarTarget
    const mainContent = document.getElementById('main-content')

    if (this.collapsedValue) {
      document.documentElement.classList.add('sidebar-state-collapsed')
      sidebar.classList.add('sidebar-collapsed')
      sidebar.classList.remove('sidebar-expanded')

      // Update main content margin
      if (mainContent) {
        mainContent.classList.remove('with-sidebar-expanded')
        mainContent.classList.add('with-sidebar-collapsed')
      }
    } else {
      document.documentElement.classList.remove('sidebar-state-collapsed')
      sidebar.classList.remove('sidebar-collapsed')
      sidebar.classList.add('sidebar-expanded')

      // Update main content margin
      if (mainContent) {
        mainContent.classList.remove('with-sidebar-collapsed')
        mainContent.classList.add('with-sidebar-expanded')
      }
    }
  }

  handleBeforeCache() {
    this.persistNavScroll({ force: true })
  }

  handleBeforeUnload() {
    this.persistNavScroll({ force: true })
  }

  persistCollapsedCookie() {
    document.cookie = `owner_sidebar_collapsed=${this.collapsedValue}; path=/; max-age=31536000; SameSite=Lax`
  }

  // Toggle submenu
  toggleSubmenu(event) {
    event.preventDefault()
    const button = event.currentTarget
    const submenu = button.nextElementSibling
    const icon = button.querySelector('[data-submenu-icon]')

    if (submenu) {
      submenu.classList.toggle('hidden')
      if (icon) {
        icon.classList.toggle('rotate-180')
      }
    }
  }

  bindNavScroll() {
    if (!this.hasNavTarget) return

    this.handleNavScroll = () => this.persistNavScroll()
    this.handleNavClick = () => this.persistNavScroll()
    this.navTarget.addEventListener('scroll', this.handleNavScroll, { passive: true })
    this.navTarget.addEventListener('click', this.handleNavClick, { capture: true })
  }

  persistNavScroll({ force = false } = {}) {
    if (!this.hasNavTarget) return
    if (!force && !this.navScrollRestored) return

    localStorage.setItem('sidebarNavScrollTop', String(this.navTarget.scrollTop))
  }

  restoreNavScroll() {
    if (!this.hasNavTarget) return

    requestAnimationFrame(() => {
      this.scrollActiveNavItemIntoView()

      requestAnimationFrame(() => {
        this.scrollActiveNavItemIntoView()
        this.navScrollRestored = true
        this.persistNavScroll()
      })
    })
  }

  scrollActiveNavItemIntoView() {
    const activeItem = this.sidebarTarget.querySelector('.sidebar-nav-item.active')
    if (!activeItem) return

    activeItem.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }
}
